import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Box, FormControl, IconButton, InputLabel, OutlinedInput, Popover, type SxProps, type Theme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { composeSx } from '../../utils/composeSx';
import { shortMonthName } from '../../utils/format';

export const HF_MONTH_LABELS: readonly string[] = Array.from({ length: 12 }, (_, index) => shortMonthName(index));

export function parseYearMonth(value: string): { year: number; month: number } {
  const [year, month] = value.slice(0, 7).split('-').map(Number);
  return { year: year || 1970, month: month || 1 };
}

export function toYearMonth(iso: string): string {
  return iso.slice(0, 7);
}

export function formatYearMonth(value: string): string {
  const { year, month } = parseYearMonth(value);
  return `${shortMonthName(month - 1)} ${year}`;
}

const MONTH_SCAN = 120;

function ym(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

function nextEnabledMonth(
  year: number,
  month: number,
  dir: -1 | 1,
  isEnabled: (year: number, month: number) => boolean,
): { year: number; month: number } | null {
  let next = { year, month };
  for (let i = 0; i < MONTH_SCAN; i += 1) {
    next = shiftMonth(next.year, next.month, dir);
    if (isEnabled(next.year, next.month)) return next;
  }
  return null;
}

function yearHasEnabledMonth(year: number, isEnabled: (year: number, month: number) => boolean): boolean {
  for (let month = 1; month <= 12; month += 1) {
    if (isEnabled(year, month)) return true;
  }
  return false;
}

function hasEnabledYear(year: number, dir: -1 | 1, isEnabled: (year: number, month: number) => boolean): boolean {
  const years = Math.ceil(MONTH_SCAN / 12);
  for (let i = 1; i <= years; i += 1) {
    if (yearHasEnabledMonth(year + dir * i, isEnabled)) return true;
  }
  return false;
}

export type HFMonthPickerProps = {
  label?: ReactNode;
  value: string;
  onChange?: (yearMonth: string) => void;
  isMonthEnabled?: (year: number, month: number) => boolean;
  formControlSx?: SxProps<Theme>;
  disabled?: boolean;
  'aria-label'?: string;
};

export function HFMonthPicker({ label, value, onChange, isMonthEnabled, formControlSx, disabled, 'aria-label': ariaLabel }: HFMonthPickerProps) {
  const labelId = useId();
  const inputId = useId();
  const selected = parseYearMonth(value);
  const [year, setYear] = useState(selected.year);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setYear(selected.year);
  }, [selected.year]);

  const enabled = useMemo(() => isMonthEnabled ?? (() => true), [isMonthEnabled]);
  const canPrevMonth = Boolean(nextEnabledMonth(selected.year, selected.month, -1, enabled));
  const canNextMonth = Boolean(nextEnabledMonth(selected.year, selected.month, 1, enabled));
  const canPrevYear = hasEnabledYear(year, -1, enabled);
  const canNextYear = hasEnabledYear(year, 1, enabled);

  function step(dir: -1 | 1) {
    const next = nextEnabledMonth(selected.year, selected.month, dir, enabled);
    if (next) onChange?.(ym(next.year, next.month));
  }

  function pick(month: number) {
    if (!enabled(year, month)) return;
    onChange?.(ym(year, month));
    setAnchor(null);
  }

  return (
    <FormControl size="small" variant="outlined" disabled={disabled} sx={composeSx({ minWidth: 188 }, formControlSx)}>
      {label != null ? (
        <InputLabel id={labelId} htmlFor={inputId} shrink>
          {label}
        </InputLabel>
      ) : null}
      <OutlinedInput
        id={inputId}
        label={label}
        notched={label != null ? true : undefined}
        readOnly
        disabled={disabled}
        value={formatYearMonth(value)}
        inputProps={{
          'aria-labelledby': label != null ? labelId : undefined,
          'aria-label': label == null ? ariaLabel : undefined,
          style: { textAlign: 'center', cursor: 'pointer', fontWeight: 600 },
        }}
        onClick={(event) => {
          if (disabled) return;
          setAnchor(event.currentTarget);
        }}
        startAdornment={
          <IconButton
            size="small"
            aria-label="Previous month"
            disabled={disabled || !canPrevMonth}
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
            sx={{ ml: -0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        }
        endAdornment={
          <IconButton
            size="small"
            aria-label="Next month"
            disabled={disabled || !canNextMonth}
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
            sx={{ mr: -0.5 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        }
        sx={{ pr: 0.5, pl: 0.5, cursor: 'pointer' }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ width: 252, p: 1.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25, fontWeight: 700, fontSize: 13 }}>
            <span>{year}</span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" aria-label="Previous year" disabled={!canPrevYear} onClick={() => setYear((current) => current - 1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" aria-label="Next year" disabled={!canNextYear} onClick={() => setYear((current) => current + 1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
            {HF_MONTH_LABELS.map((name, index) => {
              const month = index + 1;
              const on = selected.year === year && selected.month === month;
              const monthEnabled = enabled(year, month);
              return (
                <Box
                  key={name}
                  component="button"
                  type="button"
                  disabled={!monthEnabled}
                  onClick={() => pick(month)}
                  sx={{
                    height: 30,
                    borderRadius: 1,
                    border: 1,
                    borderColor: on ? 'primary.main' : 'divider',
                    bgcolor: on ? 'primary.main' : 'background.paper',
                    color: on ? 'primary.contrastText' : 'text.secondary',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: monthEnabled ? 'pointer' : 'not-allowed',
                    opacity: monthEnabled ? 1 : 0.4,
                    '&:hover': monthEnabled && !on ? { borderColor: 'primary.main', color: 'primary.main' } : undefined,
                  }}
                >
                  {name}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </FormControl>
  );
}
